import { useRef, useState } from "react";
import { CheckCircle2, ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/data/services";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MAX_FILE_MB = 5;
const MAX_FILES = 6;

const CONTACT_METHODS = ["Phone Call", "Text Message", "Email"];

export default function EstimateForm({ prefillService }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "",
    zip_code: "", description: "", preferred_contact: "Phone Call",
  });
  const [service, setService] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);
  const honeypotRef = useRef(null);

  const effectiveService = prefillService || service;

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    const valid = [];
    for (const f of picked) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name} is not an image file.`);
        continue;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${f.name} is larger than ${MAX_FILE_MB} MB.`);
        continue;
      }
      valid.push(f);
    }
    const combined = [...files, ...valid].slice(0, MAX_FILES);
    if (files.length + valid.length > MAX_FILES) {
      toast.error(`You can attach up to ${MAX_FILES} photos.`);
    }
    setFiles(combined);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("service", effectiveService);
      fd.append("website", honeypotRef.current?.value || "");
      files.forEach((f) => fd.append("photos", f));
      await api.post("/estimate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err, "We could not send your request. Please call 972-703-3267."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" data-testid="estimate-section" className="py-20 md:py-28 px-4 sm:px-6 lg:px-8 bg-surface scroll-mt-20">
      <div className="max-w-3xl mx-auto">
        <div className="text-center">
          <span className="text-brand font-bold uppercase tracking-widest text-sm">Free Estimate</span>
          <h2 className="mt-3 font-heading font-bold text-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug">
            Request Your Free On-Site Estimate
          </h2>
          <div className="mt-5 h-1.5 w-24 bg-brand rounded-full mx-auto" aria-hidden="true" />
          <p className="mt-4 text-[#4B5563] text-lg leading-relaxed">
            All estimates are free. Our team will visit your property to evaluate your
            landscaping needs and provide a personalized quote.
          </p>
          <p className="mt-2 text-sm text-[#4B5563]">
            Photos are helpful, but they do not replace the on-site visit.
          </p>
        </div>

        {submitted ? (
          <div
            className="mt-10 bg-white border border-forest/30 rounded-xl p-10 text-center"
            data-testid="estimate-confirmation"
            role="status"
          >
            <CheckCircle2 className="w-12 h-12 text-forest mx-auto" aria-hidden="true" />
            <h3 className="mt-4 font-heading font-bold text-ink text-2xl">Thank you for contacting Somoza's Landscaping.</h3>
            <p className="mt-3 text-[#4B5563] leading-relaxed">
              We have received your request and will contact you to arrange your free on-site estimate.
            </p>
            <button
              type="button"
              data-testid="estimate-submit-another-button"
              onClick={() => { setSubmitted(false); setFiles([]); setForm({ name: "", phone: "", email: "", address: "", city: "", zip_code: "", description: "", preferred_contact: "Phone Call" }); setService(""); }}
              className="mt-6 text-brand font-semibold hover:text-brand-hover transition-colors"
            >
              Submit another request
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="estimate-form"
            className="mt-10 bg-white border border-border rounded-xl p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
          >
            {/* Honeypot — hidden from humans, catches bots */}
            <div className="hidden" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input ref={honeypotRef} id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            <div className="sm:col-span-1">
              <Label htmlFor="est-name">Full Name *</Label>
              <Input id="est-name" data-testid="estimate-name-input" required maxLength={120} value={form.name} onChange={setField("name")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-1">
              <Label htmlFor="est-phone">Phone Number *</Label>
              <Input id="est-phone" data-testid="estimate-phone-input" required type="tel" value={form.phone} onChange={setField("phone")} className="mt-1.5" placeholder="214-555-0123" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="est-email">Email Address *</Label>
              <Input id="est-email" data-testid="estimate-email-input" required type="email" value={form.email} onChange={setField("email")} className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="est-address">Property Address</Label>
              <Input id="est-address" data-testid="estimate-address-input" maxLength={200} value={form.address} onChange={setField("address")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="est-city">City</Label>
              <Input id="est-city" data-testid="estimate-city-input" maxLength={100} value={form.city} onChange={setField("city")} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="est-zip">ZIP Code</Label>
              <Input id="est-zip" data-testid="estimate-zip-input" maxLength={10} value={form.zip_code} onChange={setField("zip_code")} className="mt-1.5" />
            </div>
            <div>
              <Label>Requested Service</Label>
              <Select value={effectiveService} onValueChange={setService}>
                <SelectTrigger data-testid="estimate-service-select" className="mt-1.5">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt} data-testid={`estimate-service-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Preferred Contact Method</Label>
              <Select value={form.preferred_contact} onValueChange={(v) => setForm((f) => ({ ...f, preferred_contact: v }))}>
                <SelectTrigger data-testid="estimate-contact-method-select" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="est-description">Project Description</Label>
              <Textarea
                id="est-description"
                data-testid="estimate-description-input"
                rows={4}
                maxLength={3000}
                value={form.description}
                onChange={setField("description")}
                className="mt-1.5"
                placeholder="Tell us about your project — size of the area, what you would like done, any deadlines."
              />
            </div>

            <div className="sm:col-span-2">
              <Label>Project Photos (optional)</Label>
              <div className="mt-1.5 border-2 border-dashed border-border rounded-lg p-5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onFiles}
                  className="hidden"
                  data-testid="estimate-photo-input"
                  id="est-photos"
                />
                <label
                  htmlFor="est-photos"
                  className="flex flex-col items-center gap-2 cursor-pointer text-[#4B5563] hover:text-brand transition-colors"
                >
                  <ImagePlus className="w-8 h-8" aria-hidden="true" />
                  <span className="text-sm font-medium">Add up to {MAX_FILES} photos — {MAX_FILE_MB} MB each</span>
                </label>
                {files.length > 0 && (
                  <ul className="mt-4 flex flex-wrap gap-2" data-testid="estimate-photo-list">
                    {files.map((f, idx) => (
                      <li key={`${f.name}-${idx}`} className="flex items-center gap-1.5 bg-surface rounded-full pl-3 pr-1.5 py-1 text-xs text-ink">
                        <span className="max-w-[140px] truncate">{f.name}</span>
                        <button
                          type="button"
                          aria-label={`Remove ${f.name}`}
                          data-testid={`estimate-photo-remove-${idx}`}
                          onClick={() => removeFile(idx)}
                          className="p-1 rounded-full hover:bg-border transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid="estimate-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white font-semibold rounded-full px-8 py-4 hover:bg-brand-hover transition-colors duration-200 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed text-base"
              >
                {submitting && <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />}
                {submitting ? "Sending…" : "Request My Free Estimate"}
              </button>
              <p className="mt-3 text-center text-xs text-[#9CA3AF]">
                No charge, no obligation — we will contact you to schedule your on-site visit.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
